import { useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// ── Static keyword → category map ─────────────────────────────────────────────
// Each keyword (lowercase) maps to a category name that must match DB categories
const EXPENSE_KEYWORD_MAP: Record<string, string> = {
  // Alimentação
  'restaurante': 'Alimentação',
  'almoço': 'Alimentação',
  'almo': 'Alimentação',
  'lanche': 'Alimentação',
  'pizza': 'Alimentação',
  'hamburguer': 'Alimentação',
  'hamburger': 'Alimentação',
  'ifood': 'Alimentação',
  'rappi': 'Alimentação',
  'mercado': 'Alimentação',
  'supermercado': 'Alimentação',
  'padaria': 'Alimentação',
  'café': 'Alimentação',
  'cafe': 'Alimentação',
  'refeição': 'Alimentação',
  'delivery': 'Alimentação',
  'bar': 'Alimentação',
  'churrasco': 'Alimentação',
  'jantar': 'Alimentação',
  'sushi': 'Alimentação',
  // Transporte
  'uber': 'Transporte',
  '99': 'Transporte',
  'taxi': 'Transporte',
  'táxi': 'Transporte',
  'gasolina': 'Transporte',
  'combustível': 'Transporte',
  'etanol': 'Transporte',
  'ônibus': 'Transporte',
  'metrô': 'Transporte',
  'passagem': 'Transporte',
  'pedágio': 'Transporte',
  'estacionamento': 'Transporte',
  'mecânico': 'Transporte',
  'oficina': 'Transporte',
  'revisão': 'Transporte',
  'seguro auto': 'Transporte',
  'ipva': 'Transporte',
  // Moradia
  'aluguel': 'Moradia',
  'condomínio': 'Moradia',
  'água': 'Moradia',
  'luz': 'Moradia',
  'energia': 'Moradia',
  'gás': 'Moradia',
  'internet': 'Moradia',
  'telefone': 'Moradia',
  'celular': 'Moradia',
  'iptu': 'Moradia',
  'reforma': 'Moradia',
  'manutenção': 'Moradia',
  // Saúde
  'médico': 'Saúde',
  'médica': 'Saúde',
  'consulta': 'Saúde',
  'farmácia': 'Saúde',
  'remédio': 'Saúde',
  'exame': 'Saúde',
  'plano de saúde': 'Saúde',
  'academia': 'Saúde',
  'dentista': 'Saúde',
  'fisioterapia': 'Saúde',
  'hospital': 'Saúde',
  'vacina': 'Saúde',
  // Educação
  'escola': 'Educação',
  'faculdade': 'Educação',
  'curso': 'Educação',
  'livro': 'Educação',
  'material escolar': 'Educação',
  'mensalidade': 'Educação',
  'treinamento': 'Educação',
  'workshop': 'Educação',
  // Lazer
  'cinema': 'Lazer',
  'teatro': 'Lazer',
  'show': 'Lazer',
  'viagem': 'Lazer',
  'hotel': 'Lazer',
  'pousada': 'Lazer',
  'ingresso': 'Lazer',
  'passeio': 'Lazer',
  'parque': 'Lazer',
  'game': 'Lazer',
  'jogo': 'Lazer',
  'streaming': 'Assinaturas',
  // Assinaturas
  'netflix': 'Assinaturas',
  'spotify': 'Assinaturas',
  'amazon': 'Assinaturas',
  'disney': 'Assinaturas',
  'youtube': 'Assinaturas',
  'prime': 'Assinaturas',
  'globoplay': 'Assinaturas',
  'assinatura': 'Assinaturas',
  'mensalidade': 'Assinaturas',
  // Vestuário
  'roupa': 'Vestuário',
  'calçado': 'Vestuário',
  'tênis': 'Vestuário',
  'camisa': 'Vestuário',
  'sapato': 'Vestuário',
  'bermuda': 'Vestuário',
  'moda': 'Vestuário',
  // Pets
  'veterinário': 'Pets',
  'pet shop': 'Pets',
  'ração': 'Pets',
  'banho do pet': 'Pets',
  'tosa': 'Pets',
};

const INCOME_KEYWORD_MAP: Record<string, string> = {
  'salário': 'Salário',
  'salario': 'Salário',
  'holerite': 'Salário',
  'freelance': 'Freelance',
  'freela': 'Freelance',
  'projeto': 'Freelance',
  'serviço': 'Freelance',
  'investimento': 'Investimentos',
  'dividendo': 'Investimentos',
  'rendimento': 'Investimentos',
  'juros': 'Investimentos',
  'aluguel recebido': 'Aluguel Recebido',
  'aluguel': 'Aluguel Recebido',
  'presente': 'Presente',
  'presente de': 'Presente',
  'gift': 'Presente',
  'bonus': 'Salário',
  'bônus': 'Salário',
  '13': 'Salário',
  'décimo': 'Salário',
  'comissão': 'Freelance',
  'pró-labore': 'Salário',
  'pro labore': 'Salário',
  'reembolso': 'Outros',
  'estorno': 'Outros',
};

// ── History cache (per session) ─────────────────────────────────────────────
interface HistoryEntry {
  description: string;
  category: string;
  type: string;
}

// ── Hook ───────────────────────────────────────────────────────────────────────
export const useCategorySuggestion = () => {
  const { user } = useAuth();
  const historyRef = useRef<HistoryEntry[] | null>(null);
  const loadingHistoryRef = useRef(false);

  // Lazy-load history once per session
  const ensureHistory = useCallback(async () => {
    if (historyRef.current !== null || loadingHistoryRef.current || !user) return;
    loadingHistoryRef.current = true;
    try {
      const { data } = await supabase
        .from('transactions')
        .select('description, category, type')
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(200);
      historyRef.current = (data as HistoryEntry[]) ?? [];
    } catch {
      historyRef.current = [];
    } finally {
      loadingHistoryRef.current = false;
    }
  }, [user]);

  /**
   * Suggests a category for a transaction based on description and type.
   * Returns null if no good match found.
   */
  const suggestCategory = useCallback(
    async (description: string, type: 'receita' | 'despesa'): Promise<string | null> => {
      if (!description || description.trim().length < 3) return null;

      const lowerDesc = description.toLowerCase().trim();
      const keywordMap = type === 'despesa' ? EXPENSE_KEYWORD_MAP : INCOME_KEYWORD_MAP;

      // 1. Static keyword match (most reliable)
      // Check multi-word keys first (longer = more specific)
      const sortedKeys = Object.keys(keywordMap).sort((a, b) => b.length - a.length);
      for (const key of sortedKeys) {
        if (lowerDesc.includes(key)) {
          return keywordMap[key];
        }
      }

      // 2. History match — find most frequent category for similar descriptions
      await ensureHistory();
      if (historyRef.current && historyRef.current.length > 0) {
        const words = lowerDesc.split(/\s+/).filter((w) => w.length > 3);
        if (words.length > 0) {
          const matches = historyRef.current.filter((h) => {
            if (h.type !== type || !h.category || !h.description) return false;
            const hLower = h.description.toLowerCase();
            return words.some((w) => hLower.includes(w));
          });

          if (matches.length >= 2) {
            // Find most frequent category
            const freq = matches.reduce<Record<string, number>>((acc, m) => {
              acc[m.category] = (acc[m.category] ?? 0) + 1;
              return acc;
            }, {});
            const top = Object.entries(freq).sort((a, b) => b[1] - a[1])[0];
            if (top && top[1] >= 2) {
              return top[0];
            }
          }
        }
      }

      return null;
    },
    [ensureHistory]
  );

  /**
   * Pre-warm history cache (call when modal opens)
   */
  const preWarmHistory = useCallback(() => {
    ensureHistory();
  }, [ensureHistory]);

  return { suggestCategory, preWarmHistory };
};
